import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useUserContactMutation } from '@/store/api/Users/Users.query'
import {
  kycContactUsSchema,
  type kycContactUsSchemaType,
} from '@/utils/schemas'

export const SignUpContactUs = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [submitContact] = useUserContactMutation()

  const form = useForm<kycContactUsSchemaType>({
    resolver: zodResolver(kycContactUsSchema),
    defaultValues: {
      message: '',
    },
  })

  const { handleSubmit, control } = form

  const onSubmit = async (data: any) => {
    try {
      await submitContact({
        message: data.message,
      }).unwrap()
      toast.success(t('kyc.contactUs.successMessage'))
      navigate(-1)
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('kyc.contactUs.errorMessage'))
    }
  }

  return (
    <WeScreen useHeader={false}>
      <div className="flex flex-col w-full h-full text-center items-center justify-center">
        <div className="flex flex-col w-1/2 gap-8">
          <div className="flex flex-col gap-3">
            <p className="text-neutralColor text-[40px] max-md:text-3xl font-medium">
              {t('kyc.contactUs.title')}
            </p>
            <p className="text-neutralColor80 text-2xl max-md:text-xl font-medium">
              {t('kyc.contactUs.subtitle')}
            </p>
          </div>
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-5"
            >
              <FormField
                control={control}
                name="message"
                render={({ field }) => (
                  <FormItem className="w-full rounded-md">
                    <FormControl className="flex w-full focus:outline-none">
                      <Input
                        className="rounded-3xl"
                        {...field}
                        placeholder={t('kyc.contactUs.messagePlaceholder')}
                      />
                    </FormControl>
                    <FormMessage data-name="message" />
                  </FormItem>
                )}
              />
              <div className="flex justify-end">
                <Button type="submit" className="rounded-3xl h-11 w-min">
                  {t('kyc.contactUs.submitButton')}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </WeScreen>
  )
}
